/*
Hook for managing bulk image selection and download operations
Based on useVehicleSelection pattern
Autor: Assistant
Fecha: 2025-01-28
*/
import { useState, useCallback, useMemo } from 'react'

interface UseImageSelectionProps {
  images: string[]
  onBulkDownload?: (imageUrls: string[]) => void
  onBulkDelete?: (imageUrls: string[]) => void
}

export const useImageSelection = ({ images, onBulkDownload, onBulkDelete }: UseImageSelectionProps) => {
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [selectedImageUrls, setSelectedImageUrls] = useState<string[]>([])

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (prev) {
        // Exiting selection mode, clear selections
        setSelectedImageUrls([])
      }

      return !prev
    })
  }, [])

  const handleSelectionChange = useCallback((imageUrl: string) => {
    setSelectedImageUrls((prev) => {
      if (prev.includes(imageUrl)) {
        return prev.filter((url) => url !== imageUrl)
      } else {
        return [...prev, imageUrl]
      }
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedImageUrls([...images])
  }, [images])

  const selectNone = useCallback(() => {
    setSelectedImageUrls([])
  }, [])

  const handleBulkDownload = useCallback(() => {
    if (selectedImageUrls.length > 0 && onBulkDownload) {
      onBulkDownload(selectedImageUrls)
      // Keep selection mode active so user can see progress
      // They can manually exit selection mode when done
    }
  }, [selectedImageUrls, onBulkDownload])

  const handleBulkDelete = useCallback(() => {
    if (selectedImageUrls.length > 0 && onBulkDelete) {
      onBulkDelete(selectedImageUrls)
      // Keep selection mode active so user can see progress
      // Selection will be cleared after confirmation and successful deletion
    }
  }, [selectedImageUrls, onBulkDelete])

  const selectedCount = selectedImageUrls.length
  const totalCount = images.length

  const canDownload = useMemo(() => {
    return selectedImageUrls.length > 0
  }, [selectedImageUrls])

  const canDelete = useMemo(() => {
    return selectedImageUrls.length > 0
  }, [selectedImageUrls])

  const resetSelection = useCallback(() => {
    setSelectedImageUrls([])
    setIsSelectionMode(false)
  }, [])

  const isImageSelected = useCallback(
    (imageUrl: string) => {
      return selectedImageUrls.includes(imageUrl)
    },
    [selectedImageUrls]
  )

  return {
    isSelectionMode,
    selectedImageUrls,
    selectedCount,
    totalCount,
    canDownload,
    canDelete,
    toggleSelectionMode,
    handleSelectionChange,
    selectAll,
    selectNone,
    handleBulkDownload,
    handleBulkDelete,
    resetSelection,
    isImageSelected,
  }
}

export default useImageSelection
